/**
 * Enhanced Quick Reply Generation Engine
 * Context-aware, intelligent suggestion system
 */

import { QuickReplyAction, FlowStep } from './types';

interface ConversationContext {
  messageCount: number;
  topicsDiscussed: Set<string>;
  lastUserIntent: string;
  hasSeenRecommendations: boolean;
  policyQuestionsAsked: number;
}

interface QuickReplySuggestion extends QuickReplyAction {
  score: number;
  reason: string;
}

/**
 * Extract dynamic options from AI questions
 * Improved to handle multiple formats: "X or Y", "X, Y, or Z", "either X or Y"
 */
export function extractDynamicOptions(text: string): QuickReplyAction[] {
  const replies: QuickReplyAction[] = [];
  const lowerText = text.toLowerCase();
  
  // Pattern 1: "X, Y, or Z?" format
  const listPattern = /(?:would you prefer|do you want|are you looking for|interested in)[:\s]+([^?]+)\?/i;
  const listMatch = text.match(listPattern);
  
  if (listMatch) {
    const optionsText = listMatch[1];
    // Split by comma or "or"
    const options = optionsText
      .split(/,\s*(?:or\s+)?|\s+or\s+/)
      .map(opt => opt.trim())
      .filter(opt => opt.length > 2 && opt.length < 40);
    
    if (options.length >= 2) {
      options.forEach(opt => {
        const cleaned = opt.replace(/^(a|an|the)\s+/i, '');
        const label = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        replies.push({
          label: label.length > 25 ? label.substring(0, 22) + '...' : label,
          value: `I prefer ${cleaned}`,
          nextFlowStep: FlowStep.OPEN_CHAT,
        });
      });
      return replies;
    }
  }
  
  // Pattern 2: "either X or Y" format
  const eitherPattern = /either\s+([^,]+?)\s+or\s+([^?.]+)/i;
  const eitherMatch = text.match(eitherPattern);
  
  if (eitherMatch) {
    const opt1 = eitherMatch[1].trim().replace(/^(a|an|the)\s+/i, '');
    const opt2 = eitherMatch[2].trim().replace(/^(a|an|the)\s+/i, '');
    
    if (opt1.length < 40 && opt2.length < 40) {
      replies.push(
        {
          label: opt1.charAt(0).toUpperCase() + opt1.slice(1),
          value: `I prefer ${opt1}`,
          nextFlowStep: FlowStep.OPEN_CHAT,
        },
        {
          label: opt2.charAt(0).toUpperCase() + opt2.slice(1),
          value: `I prefer ${opt2}`,
          nextFlowStep: FlowStep.OPEN_CHAT,
        }
      );
      return replies;
    }
  }

  // Pattern 3: numbered/bulleted options
  // Example:
  // "Which works better for you?\n1) Soft\n2) Medium\n3) Firm"
  const optionLines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);

  const extracted: string[] = [];
  for (const line of optionLines) {
    const m = line.match(/^(?:\d+\s*[\).:-]|[-*•])\s*(.+)$/);
    if (!m) continue;
    const opt = m[1]
      .trim()
      .replace(/[?.!]+$/g, '')
      .replace(/^(a|an|the)\s+/i, '');
    if (opt.length < 2 || opt.length > 50) continue;
    extracted.push(opt);
  }

  if (extracted.length >= 2) {
    return extracted.slice(0, 4).map((opt) => {
      const label = opt.charAt(0).toUpperCase() + opt.slice(1);
      return {
        label: label.length > 25 ? label.substring(0, 22) + '...' : label,
        value: `I prefer ${opt}`,
        nextFlowStep: FlowStep.OPEN_CHAT,
      };
    });
  }

  // Pattern 4: yes/no offers (especially when offering the quiz)
  const isYesNoOffer =
    /\b(would you like|do you want|want to|are you interested|can i help you)\b/i.test(text) &&
    /\?$/.test(text.trim());
  if (isYesNoOffer) {
    const wantsQuiz = /\b(quiz|sleep quiz|find my match|find your match|recommendations?)\b/i.test(lowerText);
    return [
      {
        label: 'Yes',
        value: wantsQuiz ? 'Yes, help me find my match' : 'Yes',
        nextFlowStep: wantsQuiz ? FlowStep.GREETING : FlowStep.OPEN_CHAT,
      },
      {
        label: 'Not right now',
        value: 'Not right now',
        nextFlowStep: FlowStep.OPEN_CHAT,
      },
    ];
  }
  
  return replies;
}

/**
 * Detect conversation completion signals
 */
export function isConversationComplete(aiResponse: string, userMessage: string): boolean {
  const completionSignals = [
    /feel free to (reach out|contact|ask)/i,
    /if you (have|need) any (other|more|further) questions/i,
    /is there anything else/i,
    /happy to help/i,
    /let me know if you need/i,
    /hope (this|that) helps/i,
  ];
  
  return completionSignals.some(pattern => pattern.test(aiResponse));
}

/**
 * Detect if AI is asking a direct question
 */
export function isAskingQuestion(text: string): boolean {
  return /\?$/.test(text.trim());
}

/**
 * Classify user intent from message
 */
export function classifyIntent(message: string): string {
  const lower = message.toLowerCase();
  
  // Shipping - expanded keywords
  if (/\b(shipping|delivery|ship|deliver|delivered)\b/.test(lower)) return 'shipping';
  if (/when will it (be delivered|arrive|get here|come)/.test(lower)) return 'shipping';
  
  if (/\b(warranty|guarantee)\b/.test(lower)) return 'warranty';
  if (/\b(return|refund|trial)\b/.test(lower)) return 'returns';
  
  // Pricing - expanded keywords
  if (/\b(price|cost|budget|cheap|expensive|afford|affordable)\b/.test(lower)) return 'pricing';
  if (/how much (does it|is it|will it)/.test(lower)) return 'pricing';
  
  if (/\b(firm|soft|plush|medium|feel)\b/.test(lower)) return 'firmness';
  
  // Cooling - expanded keywords
  if (/\b(cool|hot|temperature|heat|breathe|breathable|overheat)\b/.test(lower)) return 'cooling';
  if (/(sleep|sleeping) (hot|cool|warm)/.test(lower)) return 'cooling';
  
  // Pain - expanded keywords
  if (/\b(pain|hurt|ache|aches|sore|pressure)\b/.test(lower)) return 'pain';
  if (/(back|shoulder|neck|hip|joint) (pain|ache|hurt)/.test(lower)) return 'pain';
  if (/help with (back|shoulder|neck)/.test(lower)) return 'pain';
  if (/\b(brand|company|manufacturer)\b/.test(lower)) return 'brand';
  if (/\b(compare|difference|versus|vs)\b/.test(lower)) return 'comparison';
  if (/\b(recommend|suggest|best|top)\b/.test(lower)) return 'recommendation';
  
  return 'general';
}

/**
 * Generate context-aware quick replies with scoring
 */
export function generateSmartQuickReplies(
  aiResponse: string,
  userMessage: string,
  context: ConversationContext,
  availableBrands: string[] = []
): QuickReplyAction[] {
  const suggestions: QuickReplySuggestion[] = [];
  const lowerResponse = aiResponse.toLowerCase();
  const lowerUser = userMessage.toLowerCase();
  
  // 1. Check for dynamic options first (highest priority)
  const dynamicOptions = extractDynamicOptions(aiResponse);
  if (dynamicOptions.length > 0) {
    return dynamicOptions.slice(0, 4);
  }
  
  // 2. Check if conversation seems complete
  if (isConversationComplete(aiResponse, userMessage)) {
    // Offer next steps, not more questions
    suggestions.push(
      { label: 'Start Shopping', value: 'Show me your products', nextFlowStep: FlowStep.OPEN_CHAT, score: 10, reason: 'natural_next_step' },
      { label: 'Find My Match', value: 'Help me find the perfect mattress', nextFlowStep: FlowStep.GREETING, score: 9, reason: 'start_quiz' }
    );
    
    // Only add "Ask Another Question" if they haven't asked many policy questions
    if (context.policyQuestionsAsked < 3) {
      suggestions.push(
        { label: 'Ask Another Question', value: 'I have another question', nextFlowStep: FlowStep.OPEN_CHAT, score: 5, reason: 'continue_conversation' }
      );
    }
    
    return suggestions.sort((a, b) => b.score - a.score).slice(0, 3);
  }
  
  // 3. If AI is asking a question, don't add competing suggestions
  if (isAskingQuestion(aiResponse)) {
    return [];
  }
  
  // 4. Context-aware suggestions based on intent
  const intent = classifyIntent(userMessage);
  
  // Avoid repeating topics already discussed
  if (!context.topicsDiscussed.has(intent)) {
    switch (intent) {
      case 'pricing':
        suggestions.push(
          { label: 'Under $1000', value: 'Show me options under $1000', nextFlowStep: FlowStep.OPEN_CHAT, score: 9, reason: 'budget_specific' },
          { label: '$1000-$2000', value: 'Show me mid-range options', nextFlowStep: FlowStep.OPEN_CHAT, score: 8, reason: 'budget_specific' },
          { label: 'Premium Options', value: 'Show me your best mattresses', nextFlowStep: FlowStep.OPEN_CHAT, score: 7, reason: 'budget_specific' }
        );
        break;
        
      case 'firmness':
        suggestions.push(
          { label: 'Soft & Plush', value: 'I prefer a soft, plush feel', nextFlowStep: FlowStep.OPEN_CHAT, score: 9, reason: 'firmness_specific' },
          { label: 'Medium Feel', value: 'I like a balanced medium feel', nextFlowStep: FlowStep.OPEN_CHAT, score: 8, reason: 'firmness_specific' },
          { label: 'Firm & Supportive', value: 'I need firm support', nextFlowStep: FlowStep.OPEN_CHAT, score: 7, reason: 'firmness_specific' }
        );
        break;
        
      case 'cooling':
        suggestions.push(
          { label: 'Cooling Tech', value: 'Tell me about cooling features', nextFlowStep: FlowStep.OPEN_CHAT, score: 9, reason: 'feature_specific' },
          { label: 'Best for Hot Sleepers', value: 'What\'s best for hot sleepers?', nextFlowStep: FlowStep.OPEN_CHAT, score: 8, reason: 'feature_specific' }
        );
        break;
        
      case 'pain':
        suggestions.push(
          { label: 'Back Pain Relief', value: 'I need help with back pain', nextFlowStep: FlowStep.OPEN_CHAT, score: 9, reason: 'pain_specific' },
          { label: 'Pressure Relief', value: 'Tell me about pressure relief', nextFlowStep: FlowStep.OPEN_CHAT, score: 8, reason: 'pain_specific' }
        );
        break;
        
      case 'brand':
        if (availableBrands.length > 0) {
          availableBrands.slice(0, 3).forEach((brand, idx) => {
            suggestions.push({
              label: brand,
              value: `Tell me about ${brand}`,
              nextFlowStep: FlowStep.OPEN_CHAT,
              score: 8 - idx,
              reason: 'brand_specific'
            });
          });
        }
        break;
    }
  }
  
  // 5. Smart fallback based on conversation stage
  if (suggestions.length === 0) {
    if (context.messageCount < 3) {
      // Early conversation - guide to quiz
      suggestions.push(
        { label: 'Find My Match', value: 'Help me find the perfect mattress', nextFlowStep: FlowStep.GREETING, score: 10, reason: 'early_guidance' },
        { label: 'Browse All', value: 'Show me all your mattresses', nextFlowStep: FlowStep.OPEN_CHAT, score: 7, reason: 'exploration' }
      );
    } else if (!context.hasSeenRecommendations) {
      // Mid conversation - offer quiz
      suggestions.push(
        { label: 'Get Recommendations', value: 'Help me find my perfect mattress', nextFlowStep: FlowStep.GREETING, score: 10, reason: 'mid_guidance' }
      );
    } else {
      // Late conversation - offer shopping
      suggestions.push(
        { label: 'View Products', value: 'Show me your products', nextFlowStep: FlowStep.OPEN_CHAT, score: 10, reason: 'late_shopping' }
      );
    }
  }
  
  if (suggestions.length < 4 && !context.topicsDiscussed.has('shipping') && /\b(ship|shipping|delivery|delivered|arrive)\b/i.test(lowerResponse)) {
    suggestions.push(
      { label: 'Shipping Info', value: 'How does shipping work?', nextFlowStep: FlowStep.OPEN_CHAT, score: 7, reason: 'response_signal' }
    );
  }

  if (suggestions.length < 4 && !context.topicsDiscussed.has('warranty') && /\b(warranty|guarantee)\b/i.test(lowerResponse)) {
    suggestions.push(
      { label: 'Warranty Details', value: 'Tell me about the warranty', nextFlowStep: FlowStep.OPEN_CHAT, score: 6, reason: 'response_signal' }
    );
  }

  if (suggestions.length < 4 && !context.topicsDiscussed.has('returns') && /\b(return|returns|refund|trial)\b/i.test(lowerResponse)) {
    suggestions.push(
      { label: 'Return Policy', value: 'What is the return policy?', nextFlowStep: FlowStep.OPEN_CHAT, score: 6, reason: 'response_signal' }
    );
  }

  if (suggestions.length < 4 && /\b(financing|affirm|monthly)\b/i.test(lowerResponse)) {
    suggestions.push(
      { label: 'Financing', value: 'Do you offer financing?', nextFlowStep: FlowStep.OPEN_CHAT, score: 5, reason: 'response_signal' }
    );
  }

  // 6. Add complementary suggestions if we have room
  if (suggestions.length < 3 && !context.topicsDiscussed.has('shipping')) {
    suggestions.push(
      { label: 'Shipping Info', value: 'How does shipping work?', nextFlowStep: FlowStep.OPEN_CHAT, score: 5, reason: 'complementary' }
    );
  }
  
  if (suggestions.length < 3 && !context.topicsDiscussed.has('warranty')) {
    suggestions.push(
      { label: 'Warranty Details', value: 'Tell me about the warranty', nextFlowStep: FlowStep.OPEN_CHAT, score: 4, reason: 'complementary' }
    );
  }
  
  // Sort by score and return top 4
  return deduplicateQuickReplies(
    suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(({ label, value, nextFlowStep }) => ({ label, value, nextFlowStep }))
  );
}

/**
 * Remove semantic duplicates from quick replies
 */
export function deduplicateQuickReplies(replies: QuickReplyAction[]): QuickReplyAction[] {
  const seen = new Set<string>();
  const normalized: QuickReplyAction[] = [];
  
  // Semantic groupings for better duplicate detection
  const semanticGroups: Record<string, string> = {
    'products': 'products',
    'product': 'products',
    'mattresses': 'products',
    'mattress': 'products',
    'options': 'products',
    'items': 'products',
    'catalog': 'products',
  };
  
  for (const reply of replies) {
    // Normalize for comparison
    let key = reply.label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/^(show|see|tell|view|get|find|browse)\s+/i, '')
      .trim();
    
    // Apply semantic grouping
    for (const [word, group] of Object.entries(semanticGroups)) {
      if (key.includes(word)) {
        key = group;
        break;
      }
    }
    
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push(reply);
    }
  }
  
  return normalized;
}
